import { useState, useEffect } from "react";

// Third Party
import {
	useQueryClient,
	useMutation,
	useQuery,
	useQueries,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Formik, Field } from "formik";

// UI
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import HeadingTitleItem from "@/components/HeadingTitleItem";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import DatePicker from "@/components/ui/DatePicker";

// Icons
import { Upload } from "lucide-react";

// Services and Types
import { createCompanyDirector } from "@/services/soe";
import type { Provinces, Districts } from "@/types/master";
import {
	getProvinces,
	getDistrict,
	getBoardPositions,
} from "@/services/master";
import { handleUploadFile } from "@/services/presign-url";
import type { MutateDirector as MutateDirectorType } from "@/types/soe";
import formatDate from "@/utils/formatDate";
import { S3_BUCKET_LINK } from "@/components/constants";

export const Route = createFileRoute(
	"/_layout-private/account-management/soe/_information/director/add-new/",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const { id, data } = Route.useSearch();
	const { toast } = useToast();
	const queryClient = useQueryClient();

	// Replace params.id with localStorage
	const storageId = localStorage.getItem("companyId") ?? "";
	const companyId = storageId.replace(/^"(.+)"$/, "$1");

	// States
	const [parseData, setParseData] = useState({} as MutateDirectorType);
	const [selectedProvince, setSelectedProvince] = useState("");
	const [selectedFile, setSelectedFile] = useState("");
	const [attachFile, setAttachFile] = useState<File>();
	const [attachFileName, setAttachFileName] = useState("");

	// Queries
	const [{ data: provinces }, { data: boardPositions }] = useQueries({
		queries: [
			{
				queryKey: ["provinces"],
				queryFn: getProvinces,
			},
			{
				queryKey: ["board-positions"],
				queryFn: getBoardPositions,
			},
		],
	});

	const { data: districts } = useQuery({
		queryKey: ["districts", selectedProvince],
		queryFn: () => getDistrict(selectedProvince),
		enabled: !!selectedProvince,
	});

	// Effects
	useEffect(() => {
		if (data) {
			const parsedData = JSON.parse(data);

			console.log(JSON.parse(data), "Data");
			setParseData(parsedData);

			setSelectedProvince(parsedData?.province?.provinceCode);
		}
	}, [data]);

	// Mutations
	const createMutation = useMutation<unknown, unknown, MutateDirectorType>({
		mutationFn: (formData) => createCompanyDirector(formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["directors"] });
			toast({
				title: "ສຳເລັດ",
				description: "ເພີ່ມຂໍ້ມູນ ຄະນະອຳນວຍການ ສໍາເລັດແລ້ວ",
			});
			navigate({ to: "..", replace: true });
		},
		onError: (error) => {
			console.log(error);
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
				variant: "destructive",
			});
		},
	});

	const createUploadMutation = (
		onUploadSuccess: (filename: string | File) => void,
	) => {
		return useMutation({
			mutationFn: handleUploadFile,
			onSuccess: (filename) => {
				console.log("Upload successful, filename:", filename);

				onUploadSuccess(filename);

				toast({
					title: "ສຳເລັດ",
					description: "ອັບໂຫຼດຂໍ້ມູນສໍາເລັດແລ້ວ",
				});
			},
			onError: (error) => {
				console.error("Upload failed:", error);

				const fileInput = document.getElementById(
					"fileUrl",
				) as HTMLInputElement;
				if (fileInput) fileInput.value = "";

				toast({
					title: "ຫຼົ້ມເຫຼວ",
					description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
					variant: "destructive",
				});
			},
		});
	};

	const uploadProfileUrl = createUploadMutation((filename) =>
		setSelectedFile(filename as string),
	);
	const uploadAttachFile = createUploadMutation((filename) =>
		setAttachFileName(filename as string),
	);

	// Data
	const provinceData = provinces?.data as Provinces[];
	const districtData = districts?.data as Districts[];
	const boardPositionData = boardPositions?.data as {
		id: string;
		boardPosition: string;
	}[];

	console.log(boardPositionData, "Board Position Data");

	const mutateLoading = createMutation.isPending;

	return (
		<div className="space-y-[24px]">
			<HeadingTitleItem
				title="ຂ້ໍມູນຄະນະອຳນວຍການ"
				subTitle="ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນຄະນະອຳນວຍການຂອງລັດວິສາຫະກິດ"
			/>

			<Formik
				enableReinitialize
				initialValues={{
					companyId: companyId, // Use companyId from localStorage
					firstName: parseData?.firstName || "",
					lastName: parseData?.lastName || "",
					phone: parseData?.phone || "",
					gender: parseData?.gender || "MALE",
					birthDate:
						formatDate({
							date: parseData?.birthDate || "",
							format: "YYYY-MM-DD",
						}) || "",

					provinceId: parseData?.provinceId || "",
					districtId: parseData?.districtId || "",
					village: parseData?.village || "",
					position: parseData?.position || "",
					partyPosition: parseData?.partyPosition || "",
					prevPosition: parseData?.prevPosition || "",
					prevPartyPosition: parseData?.prevPartyPosition || "",
					prevSector: parseData?.prevSector || "",
					agreementNo: parseData?.agreementNo || "",
					agreementDate:
						formatDate({
							date: parseData?.agreementDate || "",
							format: "YYYY-MM-DD",
						}) || "",
					domesticSpecialty: parseData?.domesticSpecialty || "",
					foreignSpecialty: parseData?.foreignSpecialty || "",
					languageLevel: parseData?.languageLevel || "",
					theoryLevel: parseData?.theoryLevel || "",
					education: parseData?.education || "",
					experience: parseData?.experience || "",
				}}
				onSubmit={(values: MutateDirectorType) => {
					console.log(values);

					if (!attachFileName || !parseData?.docFile) {
						toast({
							title: "ຫຼົ້ມເຫຼວ",
							description: "ກະລຸນາອັບໂຫຼດເອກະສານແຕ່ງຕັ້ງ",
							variant: "destructive",
						});
					}

					const data = {
						...values,
						profileUrl: selectedFile || parseData?.profileUrl || "",
						docFile: attachFileName || parseData?.docFile || "",
					};

					createMutation.mutate(data);

					// setOpenModal(false);
				}}
			>
				{({ handleSubmit, setFieldValue, values }) => (
					<form onSubmit={handleSubmit} className="space-y-[24px]">
						<div>
							<Label htmlFor="profile_image_url">ຮູບພາບ</Label>
							<div className="w-[200px] h-[200px] rounded-full border border-dotted bg-zinc-50 border-zinc-200 overflow-hidden  relative flex items-center flex-col justify-center gap-[24px]">
								{selectedFile ? (
									<img
										alt="pre "
										src={S3_BUCKET_LINK + selectedFile}
										className="object-cover w-full h-full"
									/>
								) : uploadProfileUrl.isPending ? (
									<p>ກຳລັງອັບໂຫຼດຮູບພາບ...</p>
								) : (
									<>
										<Upload className="w-8 h-8" />
										<div className="text-center text-[14px]">
											<p>ອັບໂຫຼດຮູບພາບ</p>
											<p className="w-full text-muted-foreground text-wrap">
												(ຂະໜາດບໍ່ເກີນ 10MB)
											</p>
										</div>{" "}
									</>
								)}

								<input
									type="file"
									name="fileUrl"
									id="fileUrl"
									accept="image/png, image/jpg, image/jpeg"
									className="absolute inset-0 opacity-0 cursor-pointer"
									onChange={(e) => {
										if (e.target.files) {
											uploadProfileUrl.mutate(e);
										}
									}}
								/>
							</div>
						</div>
						{!selectedFile && !id && (
							<div className="flex gap-2 items-center">
								<small className="text-red-500">* ກະລຸນາເລືອກຮູບພາບ</small>
							</div>
						)}

						<div>
							<Label htmlFor="gender">ນາມ</Label>
							<RadioGroup
								name="gender"
								defaultValue={parseData?.gender || "MALE"}
								className="flex items-center gap-[40px]"
								onValueChange={(e) => setFieldValue("gender", e)}
							>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="MALE" id="MALE" />
									<Label htmlFor="MALE">ທ່ານ</Label>
								</div>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="FEMALE" id="FEMALE" />
									<Label htmlFor="FEMALE">ທ່ານ ນາງ</Label>
								</div>
							</RadioGroup>
						</div>
						<div className="grid grid-cols-2 gap-6">
							<div>
								<Label htmlFor="firstName">
									ຊື່ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									type="text"
									placeholder="ປ້ອນຊື່..."
									className="w-full"
									required
									name="firstName"
								/>
							</div>
							<div>
								<Label htmlFor="lastName">
									ນາມສະກຸນ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									type="text"
									placeholder="ປ້ອນນາມສະກຸນ..."
									className="w-full"
									required
									name="lastName"
								/>
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<DatePicker
								label="ວັນເດືອນປີເກີດ"
								name="handleChange"
								id="handleChange"
								value={values.birthDate}
								onChange={(value) => {
									setFieldValue("birthDate", value);
								}}
							/>
							<div>
								<Label htmlFor="phone">ເບີໂທ</Label>

								<Field
									as={Input}
									type="text"
									placeholder="ປ້ອນຊື່ເບີໂທ..."
									className="w-full"
									name="phone"
								/>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-4">
							<div>
								<Label htmlFor="provinceId">ແຂວງ</Label>
								<Select
									name="provinceId"
									onValueChange={(value) => {
										setFieldValue("provinceId", value);
										// Move the province code setting here
										const selectedProvinceData = provinceData?.find(
											(province) => province.id === value,
										);
										if (selectedProvinceData) {
											setSelectedProvince(selectedProvinceData.provinceCode);
										}
									}}
									value={values.provinceId}
									defaultValue={values.provinceId}
								>
									<SelectTrigger>
										<SelectValue placeholder="ເລືອກແຂວງ" />
									</SelectTrigger>
									<SelectContent>
										{provinceData?.map((province) => (
											<SelectItem
												value={province.id}
												key={province.provinceCode}
											>
												{province?.province}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label htmlFor="districtId">ເມືອງ</Label>
								<Select
									name="districtId"
									onValueChange={(value) => setFieldValue("districtId", value)}
									disabled={!selectedProvince}
									value={values.districtId}
									defaultValue={values.districtId}
								>
									<SelectTrigger>
										<SelectValue placeholder="ເລືອກເມືອງ" />
									</SelectTrigger>
									<SelectContent>
										{districtData?.map((district) => (
											<SelectItem
												value={district.id}
												key={district.districtCode}
											>
												{district?.district}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label htmlFor="village">ບ້ານ</Label>

								<Field
									as={Input}
									type="text"
									placeholder="ປ້ອນຊື່ບ້ານ..."
									className="w-full"
									name="village"
								/>
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label htmlFor="position">
									ຕຳແໜ່ງພາຍໃນວິສາຫະກິດ <span className="text-red-500">*</span>
								</Label>
								<Field
									name="position"
									as={Input}
									required
									placeholder="ປ້ອນຕຳແໜ່ງພາຍໃນວິສາຫະກິດ..."
								/>
							</div>
							<div>
								<Label htmlFor="partyPosition">ຕຳແໜ່ງພັກ</Label>
								<Field
									name="partyPosition"
									as={Input}
									placeholder="ປ້ອນຕຳແໜ່ງພັກ..."
								/>
							</div>
						</div>

						<HeadingTitleItem
							title="ຂໍ້ຕົກລົງ (ຄຜອ)"
							subTitle="ປ້ອນຂໍ້ມູນລາຍລະອຽດຂໍ້ຕົກລົງ (ຄຜອ)"
						/>

						<div className="[&>*]:w-full flex items-center justify-between gap-[32px] ">
							<div>
								<Label htmlFor="agreementNo">
									ເລກທີ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									required
									name="agreementNo"
									placeholder="ປ້ອນເລກທີ..."
								/>
							</div>
							<DatePicker
								label="ລົງວັນທີ"
								value={values.agreementDate}
								onChange={(value) => {
									setFieldValue("agreementDate", value);
								}}
							/>
						</div>

						<div className="grid grid-cols-3 gap-4">
							<div>
								<Label htmlFor="prevPosition">
									ຕຳແໜ່ງພາຍໃນວິສາຫະກິດກ່ອນຖືກແຕ່ງຕັ້ງ
								</Label>
								<Field
									as={Input}
									name="prevPosition"
									placeholder="ປ້ອນຕຳແໜ່ງພາຍໃນວິສາຫະກິດກ່ອນຖືກແຕ່ງຕັ້ງ..."
								/>
							</div>
							<div>
								<Label htmlFor="prevPartyPosition">ຕຳແໜ່ງພັກກ່ອນຖືກແຕ່ງຕັ້ງ</Label>
								<Field
									as={Input}
									name="prevPartyPosition"
									placeholder="ປ້ອນຕຳແໜ່ງພັກກ່ອນຖືກແຕ່ງຕັ້ງ..."
								/>
							</div>
							<div>
								<Label htmlFor="prevSector">ຂະແໜງທີ່ເຄີຍສັງກັດ</Label>
								<Field
									as={Input}
									name="prevSector"
									placeholder="ປ້ອນຕຳຂະແໜງທີ່ເຄີຍສັງກັດ..."
								/>
							</div>
						</div>

						<HeadingTitleItem
							title="ລະດັບການສຶກສາ"
							subTitle="ປ້ອນຂໍ້ມູນລາຍລະອຽດຂອງລະດັບການສຶກສາ"
						/>

						<div>
							<Label htmlFor="domesticSpecialty">
								ວິຊາສະເພາະພາຍໃນປະເທດ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								required
								name="domesticSpecialty"
								placeholder="ປ້ອນວິຊາສະເພາະພາຍໃນປະເທດ..."
							/>
						</div>
						<div>
							<Label htmlFor="foreignSpecialty">
								ວິຊາສະເພາະຕ່າງປະເທດ <span className="text-red-500">*</span>
							</Label>
							<Field
								name="foreignSpecialty"
								as={Input}
								required
								placeholder="ປ້ອນວິຊາສະເພາະຕ່າງປະເທດ..."
							/>
						</div>
						<div>
							<Label htmlFor="languageLevel">
								ລະດັບພາສາຕ່າງປະເທດ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								required
								name="languageLevel"
								placeholder="ປ້ອນລະດັບພາສາຕ່າງປະເທດ..."
							/>
						</div>
						<div>
							<Label htmlFor="theoryLevel">
								ລະດັບທິດສະດີການເມືອງ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								required
								name="theoryLevel"
								placeholder="ປ້ອນລະດັບທິດສະດີການເມືອງ..."
							/>
						</div>
						<div>
							<Label htmlFor="education">
								ລາຍລະອຽດປະຫວັດການສຶກສາ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Textarea}
								required
								name="education"
								placeholder="ປ້ອນລາຍລະອຽດປະຫວັດການສຶກສາ..."
							/>
						</div>
						<div>
							<Label htmlFor="experience">
								ລາຍລະອຽດປະສົບການ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Textarea}
								required
								name="experience"
								placeholder="ປ້ອນລາຍລະອຽດປະສົບການ..."
							/>
						</div>

						<div>
							<Label htmlFor="file_url">
								ເອກະສານແຕ່ງຕັ້ງ <span className="text-red-500">*</span>
							</Label>
							<div className="w-full h-[500px] rounded-lg max-h-[500px] overflow-hidden border border-dotted bg-zinc-50 border-zinc-200   relative flex items-center flex-col justify-center gap-[24px]">
								{attachFile ? (
									attachFile?.type === "application/pdf" ? (
										<embed
											src={URL.createObjectURL(attachFile)}
											type="application/pdf"
											className="object-cover w-full h-[500px]"
										/>
									) : uploadAttachFile.isPending ? (
										<p>ກຳລັງອັບໂຫຼດເອກະສານ...</p>
									) : attachFile?.type?.startsWith("image/") ? (
										<img
											alt="Preview"
											src={URL.createObjectURL(attachFile)}
											className="object-cover w-full h-full"
										/>
									) : (
										<p>ຟາຍບໍ່ຖືກຮອງຮັບ</p>
									)
								) : (
									<>
										<Upload className="w-8 h-8" />
										<div className="text-center text-[14px]">
											<p>ອັບໂຫຼດເອກະສານ ຫຼື ຮູບພາບ</p>
											<p className="w-full text-muted-foreground text-wrap">
												(ຂະໜາດບໍ່ເກີນ 10MB)
											</p>
										</div>
									</>
								)}

								{!attachFile ? (
									<input
										type="file"
										name="fileUrl"
										id="fileUrl"
										accept="image/png, image/jpg, image/jpeg, application/pdf"
										className="absolute inset-0 opacity-0 cursor-pointer"
										onChange={(e) => {
											if (e.target.files) {
												setAttachFile(e.target.files[0]);
												uploadAttachFile.mutate(e);
											}
										}}
									/>
								) : null}
							</div>
						</div>
						{attachFile ? (
							<div className="flex relative justify-center items-center mx-auto w-fit">
								<Button variant="outline">ເລືອກເອກະສານໃໝ່</Button>
								<input
									type="file"
									name="fileUrl"
									id="fileUrl"
									accept="image/png, image/jpg, image/jpeg, application/pdf"
									className="absolute inset-0 opacity-0 cursor-pointer"
									onChange={(e) => {
										if (e.target.files) {
											setAttachFile(e.target.files[0]);
											uploadAttachFile.mutate(e);
										}
									}}
								/>
							</div>
						) : null}

						<div className="flex gap-4 justify-center items-center w-full">
							<Button
								variant={"outline"}
								onClick={() =>
									navigate({
										to: "..",
										replace: true,
									})
								}
							>
								ຍົກເລີກ
							</Button>
							<Button disabled={mutateLoading} type="submit">
								{mutateLoading ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
							</Button>
						</div>
					</form>
				)}
			</Formik>
		</div>
	);
}
